import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import type { JsonValue } from '../../../../Assembler/dist/app/json/jsonValue.js';

/**
 * The key and value recovered from a raw counter island.
 */
export interface ParsedRawCounter {
    counterKey: string;
    counterValue: number;
}

/**
 * Purpose:
 * The counter capability's SHARED, PURE param-read/coerce/mutate logic — the single source of truth for the
 * counter key, the persistence opt-in, the transient key and the increment/decrement arithmetic, consumed
 * identically by every tier.
 *
 * Responsibilities:
 * - Read the CounterKey / CounterChange / Persist params off a binding's param JSON
 * - Coerce a JSON counter value to int through the one accepted spelling set
 * - Build the request-scoped transient key, apply the action arithmetic, and build/parse the raw island
 *
 * Design:
 * This type is deliberately DEPENDENCY-FREE: no file IO, no ConfigFile, no registry, no handler, and no
 * store. It exists so that no handler has to depend on another handler to reuse this logic — CounterAction
 * (server action), CounterData (server data provider), CounterClientData (server raw preparer) and
 * CounterClient (client processor) each depend ONLY on this type, never on each other. Because it is pure
 * it bundles into the browser untouched, which is what lets the client processor project the
 * byte-identical value in the browser, in --apiclient and in --ociclient (the single-source guarantee,
 * §3.7).
 *
 * The STORE is deliberately absent here: cross-request persistence is server state, so the CounterManager
 * lookup stays in the server-side handlers and never reaches the browser bundle.
 */
export class CounterFilter {
    /**
     * The component key mutated when a binding declares no CounterKey.
     */
    static readonly defaultCounterKey: string = 'Counter';

    /**
     * Reads the CounterKey param, falling back to defaultCounterKey when absent or blank.
     */
    static readCounterKey(paramJson: JsonObject | null): string {
        if (paramJson === null || paramJson === undefined || paramJson.containsKey('CounterKey') === false) {
            return CounterFilter.defaultCounterKey;
        }

        const counterKeyValue = paramJson.get('CounterKey').asString();
        if (counterKeyValue === null || counterKeyValue.trim() === '') {
            return CounterFilter.defaultCounterKey;
        }

        return counterKeyValue;
    }

    /**
     * Reads the CounterChange step, defaulting to 1 when absent or unparseable.
     */
    static readCounterChange(paramJson: JsonObject | null): number {
        if (paramJson === null || paramJson === undefined || paramJson.containsKey('CounterChange') === false) {
            return 1;
        }

        return CounterFilter.toCounterValue(paramJson.get('CounterChange'), 1);
    }

    /**
     * Reads the opt-in cross-request persistence flag from the params. Default false keeps the per-merge
     * transient behaviour; a binding with no manager wired (a client host) stays transient too. See
     * CounterManager for the durability and reset shape.
     */
    static readPersistFlag(paramJson: JsonObject | null): boolean {
        if (paramJson === null || paramJson === undefined || paramJson.containsKey('Persist') === false) {
            return false;
        }
        const persistValue = paramJson.get('Persist');
        if (persistValue.isBool === true) {
            return persistValue.getBool();
        }
        const persistText = persistValue.asString();
        return persistText !== null && persistText.toUpperCase() === 'TRUE';
    }

    /**
     * Builds the request-scoped transient key used to carry counter state across action/data passes.
     */
    static buildTransientCounterKey(componentName: string, counterKey: string): string {
        return `__CounterAction:${componentName}:${counterKey}`;
    }

    /**
     * Coerces a JSON counter value to int through the one accepted spelling set (integer, then number, then
     * parseable string), yielding the fallback when none applies. Single definition: the action, the data
     * provider and the client-data preparer all read counters through this, so a value spelling accepted by
     * one tier can never be rejected by another.
     */
    static toCounterValue(counterValue: JsonValue, fallback: number = 0): number {
        if (counterValue.isInteger === true) {
            return Math.trunc(counterValue.getInteger());
        }
        if (counterValue.isNumber === true) {
            return Math.trunc(counterValue.getNumber());
        }
        const parsedValue = parseInt(counterValue.toString(), 10);
        if (isNaN(parsedValue) === false) {
            return parsedValue;
        }
        return fallback;
    }

    /**
     * Applies the requested counter mutation. An unrecognised or absent action leaves the value unchanged,
     * so a stray Action name cannot silently corrupt the counter.
     */
    static applyCounterAction(currentCounter: number, actionText: string | null, counterChange: number): number {
        if (actionText === null || actionText === '') {
            return currentCounter;
        }

        if (actionText.toUpperCase() === 'INCREMENT') {
            return currentCounter + counterChange;
        }

        if (actionText.toUpperCase() === 'DECREMENT') {
            return currentCounter - counterChange;
        }

        return currentCounter;
    }

    /**
     * Builds the RAW counter island shipped to the browser: the resolved value plus the key it projects
     * under. The value is resolved SERVER-side on purpose — transient and persisted counter state are
     * server state, so the browser is handed the outcome, never the store.
     */
    static buildRawCounter(counterKey: string, counterValue: number): JsonObject {
        const rawDataJson = new JsonObject();
        rawDataJson.set('CounterKey', counterKey);
        rawDataJson.set('Counter', counterValue);
        return rawDataJson;
    }

    /**
     * Parses a raw counter island back into its key and value. Returns null when the island is absent or
     * malformed, so the client processor yields an empty result rather than projecting a fabricated
     * counter.
     */
    static parseRawCounter(rawJson: JsonObject | null): ParsedRawCounter | null {
        if (rawJson === null || rawJson === undefined || rawJson.containsKey('Counter') === false) {
            return null;
        }

        let counterKey = CounterFilter.defaultCounterKey;
        if (rawJson.containsKey('CounterKey') === true && rawJson.get('CounterKey').isString === true) {
            const parsedKey = rawJson.get('CounterKey').getString();
            if (parsedKey !== null && parsedKey.trim() !== '') {
                counterKey = parsedKey;
            }
        }

        return { counterKey: counterKey, counterValue: CounterFilter.toCounterValue(rawJson.get('Counter')) };
    }

    /**
     * Builds the counter capability's result JsonObject. Shared by the server GetData and the client
     * processor so the projected key is identical across tiers.
     */
    static buildCounterResult(counterKey: string, counterValue: number): JsonObject {
        const retDataJson = new JsonObject();
        retDataJson.set(counterKey, counterValue);
        return retDataJson;
    }
}
